export type TechnologyInputProductRole = "primary" | "substitute"

export type TechnologyInputProductRow = {
  productId?: string | null
  productLabel?: string
  substitutes?: Array<{
    productId?: string | null
    productLabel?: string
  }>
}

export type TechnologyInputProductUsage = {
  productId: string
  role: TechnologyInputProductRole
  inputIndex: number
  substituteIndex?: number
  productLabel?: string
  primaryLabel?: string
}

export type TechnologyInputProductConflict = {
  productId: string
  usages: TechnologyInputProductUsage[]
}

export type TechnologyInputProductTarget = {
  role: TechnologyInputProductRole
  inputIndex: number
  substituteIndex?: number
}

const sameTarget = (
  usage: TechnologyInputProductUsage,
  target?: TechnologyInputProductTarget,
) =>
  !!target &&
  usage.role === target.role &&
  usage.inputIndex === target.inputIndex &&
  usage.substituteIndex === target.substituteIndex

export const collectTechnologyInputProductUsages = (
  inputs: readonly TechnologyInputProductRow[],
) => {
  const usages = new Map<string, TechnologyInputProductUsage[]>()

  const addUsage = (usage: TechnologyInputProductUsage) => {
    const current = usages.get(usage.productId) ?? []
    current.push(usage)
    usages.set(usage.productId, current)
  }

  inputs.forEach((input, inputIndex) => {
    if (input.productId) {
      addUsage({
        productId: input.productId,
        role: "primary",
        inputIndex,
        productLabel: input.productLabel,
      })
    }

    input.substitutes?.forEach((substitute, substituteIndex) => {
      if (!substitute.productId) return
      addUsage({
        productId: substitute.productId,
        role: "substitute",
        inputIndex,
        substituteIndex,
        productLabel: substitute.productLabel,
        primaryLabel: input.productLabel,
      })
    })
  })

  return usages
}

export const findTechnologyInputProductConflicts = (
  inputs: readonly TechnologyInputProductRow[],
) => {
  const usages = collectTechnologyInputProductUsages(inputs)
  const conflicts: TechnologyInputProductConflict[] = []
  for (const [productId, productUsages] of usages) {
    if (productUsages.length > 1) {
      conflicts.push({ productId, usages: productUsages })
    }
  }
  return conflicts
}

export const findTechnologyInputProductUsagesOutsideTarget = (
  inputs: readonly TechnologyInputProductRow[],
  productId: string,
  target?: TechnologyInputProductTarget,
) => {
  const usages =
    collectTechnologyInputProductUsages(inputs).get(productId) ?? []
  return usages.filter((usage) => !sameTarget(usage, target))
}

export const formatTechnologyInputProductUsage = (
  usage: TechnologyInputProductUsage,
) => {
  if (usage.role === "primary") {
    return `jako materiał podstawowy w pozycji ${usage.inputIndex + 1}`
  }

  const owner = usage.primaryLabel ? ` dla „${usage.primaryLabel}”` : ""
  return `jako zamiennik${owner} w pozycji ${usage.inputIndex + 1}`
}
